import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Link } from 'react-router-dom';
import ImageWithFallback from './ImageWithFallback';
import { MapPin, Repeat, Tag } from 'lucide-react';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom component to update map view when center changes
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
        map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

// Listen to map zoom and drag events to sync dynamic radius and coordinates with Browse page
function MapEventsListener({ onViewportChange }) {
  const map = useMapEvents({
    zoomend() {
      handleSync();
    },
    moveend() {
      handleSync();
    }
  });

  const handleSync = () => {
    if (!onViewportChange) return;
    const center = map.getCenter();
    const currentZoom = map.getZoom();

    let calculatedRadius = 50;
    if (currentZoom >= 14) calculatedRadius = 15;
    else if (currentZoom >= 12) calculatedRadius = 35;
    else if (currentZoom >= 10) calculatedRadius = 120;
    else if (currentZoom >= 8) calculatedRadius = 400;
    else if (currentZoom >= 6) calculatedRadius = 1200;
    else calculatedRadius = 5000; // Worldwide / global mode

    onViewportChange({
      latitude: center.lat,
      longitude: center.lng,
      radius: calculatedRadius,
      zoom: currentZoom
    });
  };

  return null;
}

// Function to create a custom marker with the item's image
const createCustomIcon = (imageUrl) => {
  return L.divIcon({
    className: 'custom-item-marker',
    html: `
      <div style="
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 3px solid #3b82f6;
        background-color: #1e293b;
        background-image: url('${imageUrl || 'https://via.placeholder.com/44'}');
        background-size: cover;
        background-position: center;
        box-shadow: 0 4px 10px rgba(0,0,0,0.4);
        position: relative;
        transition: transform 0.2s;
      ">
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 8px solid #3b82f6;
        "></div>
      </div>
    `,
    iconSize: [44, 52],
    iconAnchor: [22, 52],
    popupAnchor: [0, -52],
  });
};

export default function MapView({ items = [], categories = [], center, zoom = 13, onViewportChange }) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const { currency, convertCurrency } = useCurrency();
  const currencySymbol = currency === 'ILS' ? '₪' : '$';

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c._id === catId || c.name === catId);
    if (cat) {
        return cat[`label_${language}`] || cat.label_en;
    }
    return t(catId);
  };

  // Dynamic map center resolution
  let computedCenter = center;

  if (!computedCenter || !computedCenter[0] || !computedCenter[1]) {
    // 1. Try average coordinates of displayed items
    const validItems = items.filter(i => i.location_geo?.coordinates?.length === 2 && !isNaN(i.location_geo.coordinates[0]) && !isNaN(i.location_geo.coordinates[1]));
    if (validItems.length > 0) {
      const avgLat = validItems.reduce((acc, i) => acc + i.location_geo.coordinates[1], 0) / validItems.length;
      const avgLng = validItems.reduce((acc, i) => acc + i.location_geo.coordinates[0], 0) / validItems.length;
      computedCenter = [avgLat, avgLng];
    } else {
      // 2. Try stored user location
      try {
        const stored = localStorage.getItem('user_geo_location');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.latitude && parsed.longitude) {
            computedCenter = [parsed.latitude, parsed.longitude];
          }
        }
      } catch (e) {}
    }
  }

  // Fallback to neutral global center if no items or location available
  const mapCenter = computedCenter && computedCenter[0] && computedCenter[1] ? computedCenter : [48.8566, 2.3522];

  return (
    <div className="h-full w-full rounded-2xl overflow-hidden shadow-inner border border-border relative z-0">
      <MapContainer 
        center={mapCenter} 
        zoom={zoom} 
        minZoom={3}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <ChangeView center={mapCenter} zoom={zoom} />
        <MapEventsListener onViewportChange={onViewportChange} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.esri.com/">Esri</a>'
          url={theme === 'dark' 
            ? "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
            : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          }
        />
        
        {items.map((item) => {
          const position = item.location_geo?.coordinates;
          if (!position || position.length < 2) return null;
          
          // GeoJSON is [lng, lat], Leaflet is [lat, lng]
          const leafletPos = [position[1], position[0]];
          const displayTitle = item.title;
          const displayValue = currency === 'ILS' ? convertCurrency(item.estimated_value, 'USD', 'ILS') : item.estimated_value;

          return (
            <Marker key={item._id} position={leafletPos} icon={createCustomIcon(item.images?.[0])}>
              <Popup className="custom-popup">
                <div className="w-56 sm:w-64 flex flex-col gap-2 p-1">
                  <div className="h-28 w-full rounded-lg overflow-hidden border border-border relative">
                    <ImageWithFallback src={item.images?.[0]} alt={displayTitle} className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 bg-background/90 backdrop-blur-sm text-primary font-bold text-[10px] px-2 py-0.5 rounded-full border border-primary/20 shadow-sm">
                        {currencySymbol}{displayValue.toLocaleString()}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <h4 className="font-black text-sm uppercase tracking-tighter line-clamp-1">{displayTitle}</h4>
                    
                    <div className="grid grid-cols-1 gap-1">
                        <div className="flex items-center text-[10px] text-muted-foreground">
                            <MapPin className="w-3 h-3 mr-1 flex-shrink-0 text-primary" />
                            <span className="truncate">{item.location || t('notSpecified')}</span>
                        </div>
                        <div className="flex items-center text-[10px] text-blue-400 font-bold">
                            <Repeat className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">
                                {item.looking_for?.length > 0 
                                    ? item.looking_for.map(id => getCategoryName(id)).join(', ')
                                    : t('Anything')}
                            </span>
                        </div>
                        <div className="flex items-center text-[10px] text-emerald-400 font-bold">
                            <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="capitalize">{t(item.condition)}</span>
                        </div>
                    </div>

                    <Link 
                        to={`/item/${item._id}`} 
                        className="block w-full text-center py-2 bg-primary text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all mt-2 shadow-sm"
                    >
                        {t('viewItem', 'View Item')}
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Map Legend/Overlay */}
      <div className="absolute bottom-4 left-4 z-[400] bg-background/90 backdrop-blur-md p-3 rounded-2xl border border-border shadow-lg hidden md:block">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">{t('mapDiscovery', 'Map Discovery')}</p>
        <p className="text-xs font-bold">{items.length} {t('itemsInArea', 'items in this area')}</p>
      </div>
    </div>
  );
}
